import { useEffect, useRef, useState } from "react";

// Deliberately NOT imported from track-req.tsx: the stream is a plain
// EventSource GET (no custom headers, so no CORS preflight — the stream
// rejects preflights) and must bypass the fetch wrapper. That wrapper's
// base URL also falls back to REACT_APP_BACKEND_URL, which isn't this host.
// Confirmed base per the backend team: journeybuilder.connect.dalmlabs.com,
// under /collect/v1 (same base use-track-collect.ts's REST siblings use).
const TRACK_STREAM_BASE = process.env.REACT_APP_TRACK_API_URL || "https://journeybuilder.connect.dalmlabs.com/collect/v1";

export type StreamStatus = "idle" | "connecting" | "open" | "error";

// The server's `event:` names aren't documented — a plain `data:` line
// arrives as "message"; these cover the names an event stream would most
// plausibly tag tracked events with. Heartbeat/comment lines never reach
// listeners, so they need no handling here.
const EVENT_NAMES = ["message", "event", "track", "page", "identify", "screen"];

// Opens GET {TRACK_STREAM_BASE}/stream/events?write_key=… as an EventSource
// while `enabled`, and closes it when disabled/unmounted. EventSource
// reconnects on its own (the server sends `retry: 3000`), so a dropped
// connection shows as "connecting" until it recovers; "error" means the
// browser gave up (CLOSED). `onMessage` gets each message's parsed JSON
// (or the raw string if it isn't JSON).
export default function useTrackEventStream(enabled: boolean, writeKey: string, onMessage: (data: unknown) => void) {
  const [status, setStatus] = useState<StreamStatus>("idle");
  const handlerRef = useRef(onMessage);
  handlerRef.current = onMessage;

  useEffect(() => {
    if (!enabled) {
      setStatus("idle");
      return;
    }
    setStatus("connecting");
    const es = new EventSource(`${TRACK_STREAM_BASE}/stream/events?write_key=${encodeURIComponent(writeKey)}`);

    es.onopen = () => setStatus("open");
    es.onerror = () => setStatus(es.readyState === EventSource.CLOSED ? "error" : "connecting");

    const handle = (e: Event) => {
      const raw = (e as MessageEvent).data;
      let data: unknown = raw;
      try {
        data = JSON.parse(raw);
      } catch {
        // not JSON — pass the string through
      }
      handlerRef.current(data);
    };
    EVENT_NAMES.forEach((n) => es.addEventListener(n, handle));

    return () => {
      EVENT_NAMES.forEach((n) => es.removeEventListener(n, handle));
      es.close();
    };
  }, [enabled, writeKey]);

  return { status };
}
